import axios from "axios";
import {
    DreamScript,
    DreamScriptPackage,
    ScriptProfile,
    ScriptProfileSetting,
    ScriptVersion,
} from "@/api/types";

export interface ScriptFilters {
    search?: string;
    compatibility?: 'legacy' | 'modern' | '';
    sort?: 'new' | 'downloads';
}

export interface ScriptUpload {
    scriptId: string;
    name: string;
    version: string;
    summary: string;
    description: string;
    visibility: 'PRIVATE' | 'PUBLIC';
    compatibility: Array<'legacy' | 'modern'>;
    permissions: string[];
    source: File;
}


export interface ScriptProfileInput {
    schema_version: 1;
    name: string;
    script_id: string;
    script_version: string;
    settings: ScriptProfileSetting[];
}

const localHosts = new Set(['localhost', '127.0.0.1', '::1']);
export const isDebugScriptsChannel =
    process.env.NODE_ENV !== 'production'
    || (typeof window !== 'undefined' && localHosts.has(window.location.hostname));
const scriptsRoot = '/scripts';
const scriptsUrl = (suffix = '') =>
    scriptsRoot + suffix + (isDebugScriptsChannel ? '?debug=1' : '');

export const getScripts = (filters: ScriptFilters = {}) =>
    axios.get<DreamScript[]>(scriptsUrl(), { params: filters });

export const getMine = () => axios.get<DreamScript[]>(scriptsUrl('/me'));
export const getReviewQueue = () => axios.get<DreamScript[]>(scriptsUrl('/review'));
export const getVersions = (id: number) => axios.get<ScriptVersion[]>(scriptsUrl('/' + id + '/versions'));
export const download = (id: number) => axios.get<DreamScriptPackage>(scriptsUrl('/' + id + '/download'));

export const upload = (input: ScriptUpload) => {
    const form = new FormData();
    form.append('script_id', input.scriptId);
    form.append('name', input.name);
    form.append('version', input.version);
    form.append('summary', input.summary);
    form.append('description', input.description);
    form.append('visibility', input.visibility);
    input.compatibility.forEach(value => form.append('compatibility[]', value));
    input.permissions.forEach(value => form.append('permissions[]', value));
    form.append('source', input.source);
    return axios.post<DreamScript>(scriptsUrl('/upload'), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const deleteScript = (scriptId: number) =>
    axios.delete<{ message: string }>(scriptsUrl('/' + scriptId));

export const submitToMarketplace = (scriptId: number, versionId: number) =>
    axios.post<ScriptVersion>(scriptsUrl('/' + scriptId + '/versions/' + versionId + '/marketplace'));

export const review = (scriptId: number, versionId: number, status: 'PUBLISHED' | 'REJECTED' | 'DISABLED', note = '') =>
    axios.post<ScriptVersion>(scriptsUrl('/' + scriptId + '/versions/' + versionId + '/review'), { status, note });

export const getProfiles = (scriptId: number) =>
    axios.get<ScriptProfile[]>(scriptsUrl('/' + scriptId + '/profiles'));

export const storeProfile = (scriptId: number, profile: ScriptProfileInput) =>
    axios.post<ScriptProfile>(scriptsUrl('/' + scriptId + '/profiles'), profile);

export const deleteProfile = (scriptId: number, profileId: number) =>
    axios.delete(scriptsUrl('/' + scriptId + '/profiles/' + profileId));
